/**
 * As três gavetas de "Catálogo" em Configurações > Parâmetros da loja: grade
 * de tamanhos, paleta de cores e o padrão de estoque mínimo.
 *
 * As três seguem o MESMO desenho: abrem com o dado já carregado (a tela-mãe
 * busca antes de oferecer o cartão), editam um rascunho local e só chamam a
 * API no clique de "Salvar" — sem autosave por linha, que faria uma pill
 * meio-digitada virar requisição.
 *
 * Erro de OPERAÇÃO (ex.: "tamanho em uso") fica na faixa vermelha dentro da
 * gaveta — ele descreve algo que continua errado até a pessoa agir, e um
 * toast que some em 4 s levaria embora a única explicação. Sucesso vira
 * `mostrarAviso`: a tela fecha e não há mais nada a ler ali.
 */
import { useId, useState } from 'react'

import { Botao } from '../../componentes/Botao'
import { Campo } from '../../componentes/Campos'
import { mostrarAviso } from '../../componentes/avisos'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import {
  salvarCores,
  salvarConfiguracaoCatalogo,
  salvarGrade,
  type Cor,
  type Cores,
  type ConfiguracaoCatalogo,
  type Grade,
} from '../../plataforma/catalogo'

/** A faixa de erro de operação — mesma classe do banner de `TelaConfiguracoes`. */
function FaixaDeErro({ children }: { children: string }) {
  return (
    <div role="alert" className="mb-4 rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
      {children}
    </div>
  )
}

/** Uma pill removível — o tamanho ou a cor já adicionados ao rascunho. */
function PillRemovivel({
  children,
  hex,
  aoRemover,
  rotuloRemover,
}: {
  children: string
  hex?: string
  aoRemover: () => void
  rotuloRemover: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-borda bg-superficie py-1.5 pr-1.5 pl-3 text-[13px] font-semibold text-tinta">
      {hex && (
        <span aria-hidden style={{ background: hex }} className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]" />
      )}
      {children}
      <button
        type="button"
        onClick={aoRemover}
        aria-label={rotuloRemover}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-texto-suave hover:bg-erro-suave hover:text-erro-texto"
      >
        <Icone nome="close" tamanho={13} />
      </button>
    </span>
  )
}

const HEX_VALIDO = /^#[0-9a-fA-F]{6}$/

/**
 * O campo de hex da paleta — texto livre continuava exigindo que a pessoa
 * SOUBESSE o código da cor de cabeça, e ninguém decora hexadecimal. A
 * amostra clicável ao lado abre o seletor nativo do sistema operacional (a
 * mesma picker de sempre, sem reinventar um componente): escolher visualmente
 * escreve o hex sozinho, e quem já sabe o código continua podendo digitá-lo
 * direto no campo de texto — os dois lados ficam em sincronia.
 */
function CampoHex({ value, onChange }: { value: string; onChange: (valor: string) => void }) {
  const id = useId()
  const corParaOSeletor = HEX_VALIDO.test(value) ? value : '#000000'

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-[13px] font-semibold text-texto">
        Hex (opcional)
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label="Escolher a cor visualmente"
          value={corParaOSeletor}
          onChange={(evento) => onChange(evento.target.value.toUpperCase())}
          className="h-[38px] w-[38px] shrink-0 cursor-pointer rounded-sistema border border-borda bg-superficie p-1"
        />
        <input
          id={id}
          value={value}
          onChange={(evento) => onChange(evento.target.value)}
          placeholder="#1F2937"
          className="campo w-[100px]"
        />
      </div>
    </div>
  )
}

/** Adiciona um valor a uma lista, sem duplicar (comparação exata — a
 *  normalização de caixa/espaço é feita no chamador). */
function adicionarUnico(lista: string[], valor: string): string[] {
  if (!valor || lista.includes(valor)) return lista
  return [...lista, valor]
}

// ------------------------------------------------------------------- grade

export function GavetaGrade({
  grade,
  aoFechar,
  aoSalvar,
}: {
  grade: Grade
  aoFechar: () => void
  aoSalvar: (nova: Grade) => void
}) {
  const [roupas, setRoupas] = useState(grade.roupas)
  const [numeracoes, setNumeracoes] = useState(grade.numeracoes)
  const [novaRoupa, setNovaRoupa] = useState('')
  const [novaNumeracao, setNovaNumeracao] = useState('')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  function adicionarRoupa() {
    const valor = novaRoupa.trim().toUpperCase()
    setRoupas((atual) => adicionarUnico(atual, valor))
    setNovaRoupa('')
  }

  function adicionarNumeracao() {
    const valor = novaNumeracao.trim().toUpperCase()
    setNumeracoes((atual) => adicionarUnico(atual, valor))
    setNovaNumeracao('')
  }

  async function salvar() {
    setErro('')
    setSalvando(true)
    try {
      const salva = await salvarGrade({ roupas, numeracoes })
      aoSalvar(salva)
      mostrarAviso('Grade de tamanhos salva.')
      aoFechar()
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível salvar a grade.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Gaveta rotulo="Grade de tamanhos" largura="formulario" aoFechar={aoFechar}>
      <CabecalhoDaGaveta titulo="Grade de tamanhos" subtitulo="Usada no cadastro de produto" aoFechar={aoFechar} />
      <CorpoDaGaveta>
        {erro && <FaixaDeErro>{erro}</FaixaDeErro>}

        <SecaoDeLista
          titulo="Tamanhos de roupa"
          itens={roupas}
          novoValor={novaRoupa}
          aoMudarNovoValor={setNovaRoupa}
          aoAdicionar={adicionarRoupa}
          aoRemover={(valor) => setRoupas((atual) => atual.filter((item) => item !== valor))}
          placeholder="Ex.: P, M, G, GG"
        />

        <div className="mt-6">
          <SecaoDeLista
            titulo="Numeração de calçado"
            itens={numeracoes}
            novoValor={novaNumeracao}
            aoMudarNovoValor={setNovaNumeracao}
            aoAdicionar={adicionarNumeracao}
            aoRemover={(valor) => setNumeracoes((atual) => atual.filter((item) => item !== valor))}
            placeholder="Ex.: 38, 39, 40"
          />
        </div>
      </CorpoDaGaveta>
      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </Botao>
        <Botao onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar grade'}
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}

/** A seção repetida da grade: lista de pills + campo de adicionar. */
function SecaoDeLista({
  titulo,
  itens,
  novoValor,
  aoMudarNovoValor,
  aoAdicionar,
  aoRemover,
  placeholder,
}: {
  titulo: string
  itens: string[]
  novoValor: string
  aoMudarNovoValor: (valor: string) => void
  aoAdicionar: () => void
  aoRemover: (valor: string) => void
  placeholder: string
}) {
  const idCampo = useId()

  return (
    <fieldset>
      <legend className="mb-2.5 text-[12.5px] font-semibold text-texto">{titulo}</legend>

      {itens.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {itens.map((item) => (
            <PillRemovivel key={item} aoRemover={() => aoRemover(item)} rotuloRemover={`Remover ${item}`}>
              {item}
            </PillRemovivel>
          ))}
        </div>
      )}

      <form
        className="flex gap-2"
        onSubmit={(evento) => {
          evento.preventDefault()
          aoAdicionar()
        }}
      >
        <input
          id={idCampo}
          value={novoValor}
          onChange={(evento) => aoMudarNovoValor(evento.target.value)}
          placeholder={placeholder}
          aria-label={`Novo valor — ${titulo}`}
          className="campo h-9 max-w-[220px] rounded-sistema"
        />
        <Botao type="submit" variante="secundario" icone="add" disabled={!novoValor.trim()}>
          Adicionar
        </Botao>
      </form>
    </fieldset>
  )
}

// ------------------------------------------------------------------- cores

export function GavetaCores({
  cores,
  aoFechar,
  aoSalvar,
}: {
  cores: Cores
  aoFechar: () => void
  aoSalvar: (novas: Cores) => void
}) {
  const [lista, setLista] = useState<Cor[]>(cores.cores)
  const [novoNome, setNovoNome] = useState('')
  const [novoHex, setNovoHex] = useState('')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  function adicionar() {
    const nome = novoNome.trim()
    if (!nome) return
    if (lista.some((cor) => cor.nome.toLowerCase() === nome.toLowerCase())) {
      setErro(`A cor "${nome}" já está na paleta.`)
      return
    }
    const hex = novoHex.trim()
    setLista((atual) => [...atual, hex ? { nome, hex: hex.startsWith('#') ? hex : `#${hex}` } : { nome }])
    setNovoNome('')
    setNovoHex('')
    setErro('')
  }

  async function salvar() {
    setErro('')
    setSalvando(true)
    try {
      const salva = await salvarCores({ cores: lista })
      aoSalvar(salva)
      mostrarAviso('Paleta de cores salva.')
      aoFechar()
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível salvar a paleta.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Gaveta rotulo="Cores do catálogo" largura="formulario" aoFechar={aoFechar}>
      <CabecalhoDaGaveta titulo="Cores do catálogo" subtitulo="Sugerida ao cadastrar uma variação" aoFechar={aoFechar} />
      <CorpoDaGaveta>
        {erro && <FaixaDeErro>{erro}</FaixaDeErro>}

        {lista.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {lista.map((cor) => (
              <PillRemovivel
                key={cor.nome}
                hex={cor.hex}
                aoRemover={() => setLista((atual) => atual.filter((item) => item.nome !== cor.nome))}
                rotuloRemover={`Remover ${cor.nome}`}
              >
                {cor.nome}
              </PillRemovivel>
            ))}
          </div>
        )}

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(evento) => {
            evento.preventDefault()
            adicionar()
          }}
        >
          <Campo
            rotulo="Nome da cor"
            value={novoNome}
            onChange={(evento) => setNovoNome(evento.target.value)}
            placeholder="Ex.: Preta"
            largura="curta"
          />
          <CampoHex value={novoHex} onChange={setNovoHex} />
          <Botao type="submit" variante="secundario" icone="add" disabled={!novoNome.trim()}>
            Adicionar
          </Botao>
        </form>
      </CorpoDaGaveta>
      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </Botao>
        <Botao onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar paleta'}
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}

// -------------------------------------------------------- estoque mínimo

export function GavetaConfiguracaoCatalogo({
  configuracao,
  aoFechar,
  aoSalvar,
}: {
  configuracao: ConfiguracaoCatalogo
  aoFechar: () => void
  aoSalvar: (nova: ConfiguracaoCatalogo) => void
}) {
  const [valor, setValor] = useState(String(configuracao.estoque_minimo_padrao))
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    const numero = Number(valor)
    if (!Number.isInteger(numero) || numero < 0 || numero > 999) {
      setErro('Use um número inteiro entre 0 e 999.')
      return
    }

    setErro('')
    setSalvando(true)
    try {
      const salva = await salvarConfiguracaoCatalogo({ estoque_minimo_padrao: numero })
      aoSalvar(salva)
      mostrarAviso('Padrão de estoque mínimo salvo.')
      aoFechar()
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível salvar o padrão.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Gaveta rotulo="Estoque mínimo padrão" largura="padrao" aoFechar={aoFechar}>
      <CabecalhoDaGaveta titulo="Estoque mínimo padrão" subtitulo="Aplicado a SKU novo" aoFechar={aoFechar} />
      <CorpoDaGaveta>
        <Campo
          rotulo="Estoque mínimo padrão"
          type="number"
          min={0}
          max={999}
          value={valor}
          onChange={(evento) => setValor(evento.target.value)}
          erro={erro}
          dica="Vale só para o próximo SKU cadastrado — os que já existem mantêm o próprio mínimo, ajustável no cadastro de produto."
          largura="curta"
        />
      </CorpoDaGaveta>
      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </Botao>
        <Botao onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}
